"use client";

/**
 * 🔴 2026-10-14（用户需求，三轮）：
 * ① 「分字段展示片段信息 + 按顺序自动播放预览」；
 * ② 「功能快捷入口加到右下角那栏，打开直接在上层全屏显示，整体 UI 重新排」；
 * ③ 「中间应该是播放按钮，然后还有一个连播开关，开启以后自动向后播放；
 *     另外：动作脚本/场景描述（内容）、镜头方向、视频提示词都要在这个菜单里面能展示（或收起）」。
 *
 * ## 结构（一个文件两个导出，避免两份实现）
 *
 *   <ShotPreviewEntry />     ← 右下角快捷入口（小胶囊），住在 task-queue-dock 那一栏里
 *   <ShotPreviewOverlay />   ← 上层全屏弹层（z-[80]，高于抽屉 z-60 / 预览 z-50 / 任务浮窗 z-50）
 *
 * 入口与弹层分居两个组件树，靠 `useShotPreviewStore` 的 `open` 联动；
 * **shots / 成片地址只由页面注入**，store 里一个镜头字段都没有（避免第二份事实）。
 *
 * ## 传输控件（transport）语义分离：播放 ≠ 连播
 *
 * 🔴 用户第三轮明确拆开：中间那个键**只管当前这一镜播/停**（`togglePlay`），
 * 「自动向后播放」是**另一件独立的事**（`loopNext` 开关）。
 * 早期版本把两者揉成一个「一键顺序播放」大按钮 ⇒ 用户按一下连播状态被隐式改掉，
 * 想「反复单曲循环」或「看一镜就停」都做不到。现在：
 *
 * | 控件       | 变量          | 语义                                        |
 * |------------|---------------|---------------------------------------------|
 * | 中央大键   | `playing`     | 当前镜 播/停（空格）                        |
 * | 连播开关   | `loopNext`    | 本镜播完**自动进下一镜**（到尾停，不循环）  |
 * | 上一/下一  | —             | 跳转（跳转后按 `loopNext` 决定播完是否前进）|
 *
 * `handleEnded` 只看 `loopNextRef`：`false` 时播完停在原地（配合播放/暂停键形成单曲反复），
 * `true` 时前进一镜；到尾一律停（与换镜 clamp 同口径，不环形）。
 *
 * ## 字段区（右栏）：三标签切换 + 三块常显
 *
 * 🔴 第四轮用户原话：「底部的菜单 移动到右边；场景描述/动作脚本/视频提示词 改成3个标签切换，
 * 下方默认显示：动作脚本 信息，对白、音效、镜头方向 这些信息直接显示，不用折叠」。
 *
 * 右栏自上而下：
 *
 * | 区块       | 来源                  | 形态 |
 * |------------|-----------------------|------|
 * | 三标签     | `TABS`                | **动作脚本（默认）** / 场景描述 / 视频提示词 |
 * | 标签正文   | 见 `TABS[].read`      | `flex-1` 滚动区，`data-shot-preview-tab-body` |
 * | 镜头方向   | `shot.cameraDirection`| 常显，不折叠 |
 * | 对白       | `shot.dialogues[]`    | 常显，按 `sequence` 升序（与分镜卡声音带同口径） |
 * | 音效       | `shot.soundDesign`    | 常显；🔴 不显示 `musicCue`（全剧禁乐的合规占位，用户 2026-10-09 拍板） |
 * | 镜号条     | `playable`            | **从底部搬上来的**，横向滚动 |
 * | 走带       | 上一·播放·下一/静音/连播 | **从底部搬上来的**，播放键仍在中间 |
 *
 * 🔴 `TABS` 定义表是 label↔字段的**唯一映射**，别在 JSX 里另写一份（必然漂移）。
 * 第三轮那套「四组各自独立折叠」已按用户要求废除，别回退。
 *
 * ## 全屏播放（第五轮，替代原「精简模式」）
 *
 * 🔴 用户原话：「移除精简模式，改成全屏播放功能」。
 * 精简模式只是把右栏藏起来（`compact` 布尔量控制两个分支），**不是真全屏**；
 * 现在改用**浏览器 Fullscreen API**，全屏目标是**播放器容器 `stageRef`**（不是整个弹层）
 * —— 全屏时只剩画面，右栏/顶栏不参与铺满。
 *
 * | 操作        | 行为 |
 * |-------------|------|
 * | 点「全屏播放」/ 按 `F` | 进入/退出全屏 |
 * | 全屏中按 `Esc`          | **只退出全屏**，不关弹层（否则一下 Esc 把播放器整个关掉） |
 * | 非全屏按 `Esc`          | 关闭弹层 |
 * | 关闭弹层                | **先退出全屏再关**，否则全屏元素随 DOM 消失 → 黑屏残留 |
 *
 * 🔴 退出必须**双路**：按钮 + `fullscreenchange` 监听。只做按钮会漏掉用户按 Esc/F11
 * 等原生退出 ⇒ 按钮状态与实际不符（UI 说「全屏播放」但其实还在全屏里）。
 * 🔴 全屏中顶栏不在全屏目标内，必须额外浮一个「退出全屏」入口，否则进去就找不到怎么出来。
 *
 * ## 状态同步与播放重置
 *
 * **单一状态源** `activeShotId`：字段区与播放器都由它派生 → 不会出现
 * 「文字是镜 12、画面是镜 13」。抽屉打开哪一镜，弹层就跟到哪一镜（页面侧同步）。
 *
 * **切镜重置**：`<video key={src}>` 重挂 + `currentTime = 0` 显式归零（浏览器复用元素会残留进度）；
 * 播放中切镜**续播**，暂停时切镜**保持暂停**（不自动出声）。
 * 🔴 该 effect 依赖**只挂 `[activeShotId, open]`**，播放态读 `playingRef` 镜像 ——
 * 把 `playing` 写进依赖会让「暂停」也跑一遍，把进度冲回 0。
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Clapperboard,
  MessageCircle,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  RefreshCw,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { previewUrl } from "@/lib/utils/thumb-url";
import {
  clampPlaylistIndex,
  lowerBoundBySequence,
  playlistIndexOf,
} from "@/lib/video/playlist-index";
import { useShotPreviewStore } from "@/stores/shot-preview-store";
import { useVideoAudioStore } from "@/stores/video-audio-store";
import { ShotTextBlocks } from "@/components/editor/shot-text-blocks";
import type { Shot } from "@/stores/project-store";

/* ────────────────────────────── 入口按钮 ────────────────────────────── */

/**
 * 右下角快捷入口（住在 task-queue-dock 那一栏里，折叠/展开两态都渲染）。
 *
 * 🔴 2026-10-17：改成**扁平分段**（无独立边框/阴影），与「回顶」「后台任务」拼进
 *   同一个胶囊容器里 —— 旧版是三块各自带 border + shadow-lg 的白药丸飘在角落，
 *   看起来像三个互不相干的小挂件；分段化之后只有容器有一圈边，视觉上是一个整体。
 *   标签与 aria 不变（实机脚本与门禁靠它们定位）。
 */
export function ShotPreviewEntry() {
  const show = useShotPreviewStore((s) => s.show);
  return (
    <button
      type="button"
      onClick={() => show()}
      data-shot-preview-entry="1"
      aria-label="顺序预览播放"
      title="顺序预览播放（逐镜连续播放 + 分字段信息）"
      className="flex h-6 items-center gap-1 rounded-full px-2 text-[10px] font-semibold text-gray-600 transition-colors hover:bg-[var(--surface)] hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <Play className="h-3 w-3 shrink-0 text-primary" />
      <span className="whitespace-nowrap">顺序预览</span>
    </button>
  );
}

/* ────────────────────────────── 全屏弹层 ────────────────────────────── */

export interface ShotPreviewOverlayProps {
  shots: Shot[];
  /** 🔴 由页面注入的成片地址解析（与分镜卡/VideoStage 同一口径，必须已 uploadUrl） */
  getVideoUrl: (shot: Shot) => string | null;
  /** 当前聚焦镜头（单一状态源：字段区与播放器都由它派生） */
  activeShotId: string | null;
  onActiveShotChange: (id: string) => void;
  /**
   * 🔴 2026-10-14「同步信息刷新」：重新拉数据源。
   * 由页面注入、复用它自己的 `refreshProject` —— **弹层内不得自己发请求**，
   * 否则就成了第二份加载逻辑（口径漂移且无报错）。
   */
  onRefresh?: () => Promise<void> | void;
}

/** 🔴 2026-10-14 第四轮：右栏三个标签（动作脚本 / 场景描述 / 视频提示词） */
type TabKey = "action" | "scene" | "video";

/** 🔴 标签定义表 —— label ↔ 字段的唯一映射（JSX 里不得另写一份） */
const TABS: ReadonlyArray<{
  key: TabKey;
  label: string;
  read: (shot: Shot) => string | null | undefined;
  empty: string;
}> = [
  { key: "action", label: "动作脚本", read: (s) => s.motionScript, empty: "动作脚本未生成" },
  { key: "scene", label: "场景描述", read: (s) => s.prompt, empty: "场景描述未生成" },
  { key: "video", label: "视频提示词", read: (s) => s.videoPrompt, empty: "视频提示词未生成" },
];

/** 🔴 第四轮：默认落在「动作脚本」（用户指定） */
const DEFAULT_TAB: TabKey = "action";

export function ShotPreviewOverlay({
  shots,
  getVideoUrl,
  activeShotId,
  onActiveShotChange,
  onRefresh,
}: ShotPreviewOverlayProps) {
  const open = useShotPreviewStore((s) => s.open);
  const hide = useShotPreviewStore((s) => s.hide);

  const [playing, setPlaying] = useState(false);
  /** 连播开关：与 `playing` **语义独立**（见文件头）——本镜播完是否自动进下一镜 */
  const [loopNext, setLoopNext] = useState(true);
  /**
   * 🔴 2026-10-22（用户：「这个声音开关是全局生效的，状态也需要落库」）。
   *
   * 静音态**不再自己存 localStorage**，改用全站唯一的 `useVideoAudioStore`
   * （与分镜卡 / 预览页播放器同一份，落库到 `app_settings`）。
   *
   * 为什么必须收口：留着 localStorage 就等于**两个开关各记各的** ——
   * 在这个弹层里静音、关掉弹层后分镜卡里的播放器还在响，
   * 用户没法理解「到底关没关」。旧的 `shot-preview-muted` 键就此作废
   * （继续读它反而会把用户拽回旧状态）。
   *
   * 默认仍是静音（浏览器拒绝未静音自动播放），但用户的选择现在**全站共享 + 落库**。
   */
  const globalMuted = useVideoAudioStore((s) => s.muted);
  const hydrateAudio = useVideoAudioStore((s) => s.hydrate);
  const setGlobalMuted = useVideoAudioStore((s) => s.setMuted);
  useEffect(() => {
    void hydrateAudio();
  }, [hydrateAudio]);
  const muted = globalMuted ?? true;
  const toggleMuted = useCallback(() => setGlobalMuted(!muted), [setGlobalMuted, muted]);
  const [tab, setTab] = useState<TabKey>(DEFAULT_TAB);
  /** 🔴 第五轮：「精简模式」已废除，改为**全屏播放**（浏览器 Fullscreen API） */
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState<string | null>(null);
  /**
   * 🔴 第六轮：新同步进来的成片，在镜号条上标 `new`，**点它或播到它就消失**。
   * 存「该镜的成片 URL」而不是 shotId —— 同一镜重渲染出片（同 id、URL 变了）也应算新，
   * 按 id 记会漏掉「刚更新的那一版」。
   */
  const [freshSrcs, setFreshSrcs] = useState<ReadonlySet<string>>(new Set());
  /** 刷新前已有成片 URL 的快照（刷新前存、shots 变化后消费一次即清） */
  const prevSrcsRef = useRef<Set<string> | null>(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const playingRef = useRef(playing);
  playingRef.current = playing;
  const loopNextRef = useRef(loopNext);
  loopNextRef.current = loopNext;
  /** 🔴 全屏目标 = 播放器容器（不是整个弹层）：全屏时只剩画面，右栏与顶栏都不参与 */
  const stageRef = useRef<HTMLDivElement>(null);

  /**
   * 🔴 第五轮：全屏播放。替代原来的「精简模式」。
   *
   * · 必须用**浏览器 Fullscreen API**，不能用 CSS 假装铺满 —— 假全屏骗不过用户
   *   （不进浏览器全屏就无法用系统级快捷键 / 多显示器 / 空格暂停等原生行为）。
   * · 全屏目标是**播放器容器**而不是整个弹层：全屏时右栏/顶栏不该跟着铺满。
   * · 退出必须双路：`fullscreenchange`（用户按 Esc / F11 等原生退出）与按钮点击。
   *   只做按钮会漏掉系统级退出 ⇒ 按钮状态与实际不符（UI 说没全屏、实际还在）。
   * · 关闭弹层前先退出全屏，否则会把黑屏留在用户屏幕上（DOM 没了全屏元素仍在）。
   */
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      // 卸载/关闭时兜底退出全屏，避免留下全屏黑屏
      if (document.fullscreenElement && document.exitFullscreen) {
        void document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {});
      return;
    }
    // Safari 老版用 webkitRequestFullscreen；缺 API 时如实静默失败即可（按钮不会有假状态）
    const req = el.requestFullscreen ?? (el as unknown as { webkitRequestFullscreen?: () => Promise<void> }).webkitRequestFullscreen;
    if (req) void req.call(el).catch(() => {});
  }, []);

  /** 关闭弹层：先退全屏再关（否则全屏元素随 DOM 消失 → 黑屏残留） */
  const closeOverlay = useCallback(() => {
    if (document.fullscreenElement && document.exitFullscreen) {
      void document.exitFullscreen().catch(() => {});
    }
    hide();
  }, [hide]);

  /** 可播清单：按镜号升序，只含有成片的镜 —— 顺序播放天然跳过没出片的镜 */
  const playable = useMemo(
    () =>
      [...shots]
        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
        .map((shot) => ({ shot, src: getVideoUrl(shot) }))
        .filter((x): x is { shot: Shot; src: string } => !!x.src),
    [shots, getVideoUrl]
  );

  const activeShot = useMemo(
    () => shots.find((s) => s.id === activeShotId) ?? null,
    [shots, activeShotId]
  );
  const activeSrc = activeShot ? getVideoUrl(activeShot) : null;
  const activeIdx = playlistIndexOf(playable, activeShotId);

  // 首次打开且用户没选过 → 落到第一镜（有 `touchedRef` 记忆，不抢用户后续选择）
  const touchedRef = useRef(false);
  useEffect(() => {
    if (!open) return;
    if (activeShotId || touchedRef.current || playable.length === 0) return;
    onActiveShotChange(playable[0].shot.id);
  }, [open, activeShotId, playable, onActiveShotChange]);

  /** 片段切换 → 播放状态重置（依赖只挂 activeShotId，原因见文件头） */
  useEffect(() => {
    if (!open) return;
    const el = videoRef.current;
    setProgress(0);
    setDuration(0);
    if (!el) return;
    el.currentTime = 0;
    if (playingRef.current && activeSrc) el.play().catch(() => setPlaying(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeShotId, open]);

  // 打开时锁背景滚动；Esc 关闭（挂在 document：焦点可能在 video 上，容器收不到）
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      // 🔴 第五轮：全屏中按 Esc 只退出全屏，**不关弹层**（否则一下 Esc 就把播放器整个关掉）
      if (e.key === "Escape") {
        if (document.fullscreenElement) {
          void document.exitFullscreen?.().catch(() => {});
          return;
        }
        closeRef.current();
      } else if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        fullscreenRef.current();
      } else if (e.key === "ArrowLeft") jumpRef.current(-1);
      else if (e.key === "ArrowRight") jumpRef.current(1);
      else if (e.key === " ") {
        e.preventDefault();
        toggleRef.current();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hide]);

  /** 清掉某片的 `new` 标记（点它 / 播到它都调这里）—— 声明须在 `jump` 之前（它要调 clearFresh） */
  const clearFresh = useCallback((src: string | null) => {
    if (!src) return;
    setFreshSrcs((s) => {
      if (!s.has(src)) return s;
      const next = new Set(s);
      next.delete(src);
      return next;
    });
  }, []);

  const jump = useCallback(
    (delta: number) => {
      if (playable.length === 0) return;
      touchedRef.current = true;
      // 当前镜没成片（idx=-1）时按镜号二分找基准，避免「点下一镜跳回第一镜」
      const base =
        activeIdx >= 0 ? activeIdx : lowerBoundBySequence(playable, activeShot?.sequence ?? 0);
      const target = playable[clampPlaylistIndex(base, playable.length, delta)];
      clearFresh(target.src); // 跳过去 = 看过，清 new
      onActiveShotChange(target.shot.id);
      setPlaying(true); // 「跳转播放」
    },
    [playable, activeIdx, activeShot, onActiveShotChange, clearFresh]
  );

  /**
   * 本镜播完：**先看连播开关**。
   * · 关 → 停在原地（配中央播放键形成单曲反复）
   * · 开 → 前进一镜；到尾即停（不循环，与换镜 clamp 同口径）
   *
   * 🔴🔴 **绝不能用 `playingRef.current` 当闸门**（2026-10-14 真机取证，用户原话「连播没有生效」）。
   * Chrome 播到结尾的事件顺序实测是 **`pause` → `ended`**：
   *   pause 先到 → `onPause` 把 `playing` 置 false → `playingRef.current` 变 false
   *   → 紧接着的 `ended` 进到这里被 `if (!playingRef.current) return` 早退 ⇒ 连播**永不前进**。
   * `ended` 事件本身就是「这一镜放完了」的权威信号，闸门只该是**连播开关**。
   *
   * 🔴 切下一镜前必须先 `setPlaying(true)`：切镜 effect 用 `playingRef` 决定要不要续播，
   * 而此刻它已被上面的 `pause` 置成 false，不补回去下一镜就是「切了但不出声」。
   */
  const handleEnded = useCallback(() => {
    if (!loopNextRef.current) return;
    // 只认「自然播到结尾」：用户把进度条拖到片尾也会触发 ended，那种不该跳镜
    const el = videoRef.current;
    if (el && Number.isFinite(el.duration) && el.duration > 0) {
      if (el.currentTime < el.duration - 0.35) return;
    }
    if (activeIdx >= 0 && activeIdx < playable.length - 1) {
      setPlaying(true); // 补回被 pause 打掉的播放态（切镜 effect 靠它续播）
      onActiveShotChange(playable[activeIdx + 1].shot.id);
    } else {
      setPlaying(false);
    }
  }, [activeIdx, playable, onActiveShotChange]);

  const togglePlay = useCallback(() => {
    const el = videoRef.current;
    if (!el || !activeSrc) return;
    touchedRef.current = true;
    if (el.paused) el.play().catch(() => setPlaying(false));
    else el.pause();
  }, [activeSrc]);

  /**
   * 「同步信息」刷新：把数据源重新拉一遍（新成片 / 新提示词 / 新对白都会即时反映）。
   *
   * · 只调页面注入的 `onRefresh`（复用它的 `refreshProject`）—— 弹层内不自发请求。
   * · 刷新期间**不打断播放**：`shots` 变化不会重挂 `<video>`（key 只跟 `activeSrc` 走）。
   * · 失败要如实报，不能静默吞掉：用户按了按钮必须有反馈。
   */
  const handleRefresh = useCallback(async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    // 🔴 先记下「刷新前已有的成片 URL」，刷完与之比对 = 新同步进来的片
    const before = new Set(playable.map((p) => p.src));
    try {
      await onRefresh();
      setRefreshedAt(
        new Date().toLocaleTimeString("zh-CN", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" })
      );
      // 🔴 刷新后 `playable` 还是旧值（等 props 下发），所以差集在下面那个
      //    「shots 变化 ⇒ 重算 fresh」的 effect 里做，这里只负责留快照。
      prevSrcsRef.current = before;
    } catch (err) {
      console.error("[ShotPreviewOverlay] 刷新失败", err);
    } finally {
      setRefreshing(false);
    }
  }, [onRefresh, refreshing, playable]);

  /**
   * 🔴 `shots` 变化后重算「新片」标记。
   * 🔴 判据必须是**URL 差集**而不是「id 不在旧集合」：同一镜重新出片时 id 不变、URL 变了，
   *    按 id 记会把「刚更新的那一版」漏掉。
   */
  useEffect(() => {
    const before = prevSrcsRef.current;
    if (!before) return;
    prevSrcsRef.current = null;
    const added = playable.filter((p) => !before.has(p.src)).map((p) => p.src);
    if (added.length > 0) setFreshSrcs((s) => new Set([...s, ...added]));
  }, [playable]);

  // 给键盘监听用的镜像（避免把 jump/toggle 塞进那个 effect 的依赖）
  const jumpRef = useRef(jump);
  jumpRef.current = jump;
  const toggleRef = useRef(togglePlay);
  toggleRef.current = togglePlay;
  const closeRef = useRef(closeOverlay);
  closeRef.current = closeOverlay;
  const fullscreenRef = useRef(toggleFullscreen);
  fullscreenRef.current = toggleFullscreen;

  const fmt = (sec: number) => {
    if (!Number.isFinite(sec) || sec < 0) sec = 0;
    return `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, "0")}`;
  };

  const dialogues = [...(activeShot?.dialogues ?? [])].sort((a, b) => a.sequence - b.sequence);
  const position = activeIdx >= 0 ? `${activeIdx + 1} / ${playable.length}` : "—";
  const isLast = activeIdx >= 0 && activeIdx === playable.length - 1;

  // 🔴 播到/切到某镜 ⇒ 它已「看过」，`new` 立即消失（用户原话：点击或该分镜已经播放后消失）
  useEffect(() => {
    if (activeSrc) clearFresh(activeSrc);
  }, [activeSrc, clearFresh]);

  if (!open) return null;

  return (
    <div
      data-shot-preview-overlay="root"
      className="fixed inset-0 z-[80] flex flex-col bg-[#0d0d10]/96 backdrop-blur-sm"
    >
      {/* ── 顶栏：镜号 / 位置 / 播放态 / 精简切换 / 关闭 ── */}
      <div className="flex items-center gap-3 border-b border-white/10 px-4 py-2.5 text-white">
        <Clapperboard className="h-4 w-4 text-white/70" />
        <span className="font-mono text-sm font-bold tabular-nums">
          {activeShot ? `镜 ${activeShot.sequence}` : "—"}
        </span>
        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] tabular-nums text-white/80">
          {position}
        </span>
        <span className="text-[11px] text-white/55" data-shot-preview-status="1">
          {!activeSrc
            ? "本镜暂无成片"
            : playing
              ? loopNext
                ? "连播中"
                : "单曲播放中"
              : "已暂停"}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          {/* 🔴 2026-10-14「同步信息」：重新拉数据源（新成片/新提示词/新对白即时反映）。
              复用页面注入的 refreshProject，弹层内不自发请求。刷新中转圈且禁用，防连点。 */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={!onRefresh || refreshing}
            aria-label="同步信息刷新"
            title="重新拉取分镜数据（新成片 / 新提示词 / 新对白）"
            data-shot-preview-refresh="1"
            className="flex h-7 items-center gap-1.5 rounded-lg bg-white/10 px-2 text-[11px] transition-colors hover:bg-white/20 disabled:opacity-40"
          >
            <RefreshCw
              className={cn("h-3.5 w-3.5", refreshing && "animate-spin")}
              data-shot-preview-refreshing={refreshing ? "1" : undefined}
            />
            {refreshing ? "同步中" : refreshedAt ? `已同步 ${refreshedAt}` : "同步信息"}
          </button>
          {/* 🔴 第五轮：「精简模式」已废除 → **全屏播放**（浏览器 Fullscreen API，F 键或点此进入）*/}
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-pressed={isFullscreen}
            data-shot-preview-fullscreen="1"
            title="全屏播放（F）"
            className={cn(
              "flex h-7 items-center gap-1.5 rounded-lg px-2 text-[11px] transition-colors",
              isFullscreen ? "bg-white/25" : "bg-white/10 hover:bg-white/20"
            )}
          >
            {isFullscreen ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
            {isFullscreen ? "退出全屏" : "全屏播放"}
          </button>
          <button
            type="button"
            onClick={closeOverlay}
            aria-label="关闭顺序预览"
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/10 transition-colors hover:bg-white/25"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ── 主区：左大播放器 + 右字段栏 ── */}
      <div
        data-shot-preview-body="1"
        /**
         * 🔴🔴 2026-10-16 移动端事故修复：「看不到播放器区域」。
         *
         * 原 `className="flex min-h-0 flex-1 gap-4 px-4 py-4 lg:flex-row"` ——
         * **基线方向缺失**：flex 默认 row，只写了 `lg:flex-row`（冗余）。
         * ⇒ 移动端也是 **row**，而右栏是 `w-full`（= 容器整宽）⇒ 播放器列被挤成 **0 宽**，
         *   肉眼就是「播放器看不见」，但结构门禁全绿（它只锁了横向溢出的 `min-w-0`）。
         * 与 2026-10-14 那次「镜号条撑爆」是**同一族**：门禁锁了成因而漏了另一条成因。
         * 现：基线 `flex-col`（竖排：播放器在上、字段栏在下），`lg:flex-row` 保持桌面横排。
         */
        className="flex min-h-0 flex-1 flex-col gap-3 px-3 py-3 lg:flex-row lg:gap-4 lg:px-4 lg:py-4"
      >
        {/* 播放器
            🔴 min-w-0 不是可选的：镜号条是横向滚动容器（93 镜 ≈ 7300px 内容宽），
            flex 子项默认 min-width:auto 会让它把整列撑爆 → 右侧字段栏被推到 x≈7000，
            肉眼就是「字段看不见 / 点不到」，而结构门禁全绿（本例即如此）。
            🔴 `stageRef` 是**全屏目标**（第五轮）：全屏时只有画面，右栏不参与。
            🔴 `relative` 是必需的：全屏时**只有全屏元素内部会渲染**，退出按钮必须挂在这里
               —— 挂在全屏元素之外，真机上它不可见也点不到（Playwright 能 boundingBox 但点击被全屏元素吃掉）。 */}
        <div
          ref={stageRef}
          data-shot-preview-stage="1"
          className="relative flex min-h-[36vh] min-w-0 flex-1 flex-col lg:min-h-0"
        >
          {/* 全屏内退出入口：必须在 stage 内（见上），Esc 之外再给一个看得见的按钮 */}
          {isFullscreen && (
            <button
              type="button"
              onClick={toggleFullscreen}
              data-shot-preview-fullscreen-exit="1"
              aria-label="退出全屏播放"
              className="absolute right-4 top-4 z-10 flex h-8 items-center gap-1.5 rounded-full bg-black/60 px-3 text-[11px] font-medium text-white backdrop-blur transition-colors hover:bg-black/80"
            >
              <Minimize2 className="h-3.5 w-3.5" />
              退出全屏（Esc）
            </button>
          )}
          {activeSrc ? (
            <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-2xl bg-black">
              <video
                key={activeSrc}
                ref={videoRef}
                src={previewUrl(activeSrc)}
                muted={muted}
                playsInline
                preload="metadata"
                aria-label={`片段 ${activeShot?.sequence ?? ""} 预览`}
                className="max-h-full w-full cursor-pointer object-contain"
                onClick={togglePlay}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={handleEnded}
                onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
                onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
              />
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center rounded-2xl border border-dashed border-white/20 text-sm text-white/50">
              本镜暂无成片（只有已出片的镜参与播放）
            </div>
          )}

          {/* 进度条 + 时间码 */}
          <div className="mt-2 flex items-center gap-2.5">
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={Math.min(progress, duration || 0)}
              onChange={(e) => {
                const el = videoRef.current;
                const v = Number(e.target.value);
                if (el) el.currentTime = v;
                setProgress(v);
              }}
              aria-label="播放进度"
              className="h-1.5 min-w-0 flex-1 cursor-pointer appearance-none rounded-full bg-white/25 accent-white"
            />
            <span className="font-mono text-[11px] tabular-nums text-white/70">
              {fmt(progress)} / {fmt(duration)}
            </span>
          </div>
        </div>

            {/* 🔴 第四轮：右栏 = 标签字段区 + 镜头方向 + 对白 + 音效 + 镜号条 + 走带
                （底部那条整块搬上来了；用户原话「底部的菜单 移动到右边」）
                🔴🔴 2026-10-16 移动端展示逻辑重做（老板：动作脚本等内容区太短）

                **旧逻辑的错**：给右栏设 `max-h-[44vh]`，而栏内 镜头方向/对白/音效/镜号条/走带
                  全是 `shrink-0`（只有它们有高度保障），可伸缩的**标签正文** `flex-1 min-h-0`
                  被一路挤到 **≈0 高** —— 用户要读的「动作脚本」反而没地方显示。
                  （手机上 44vh ≈ 300px，固定块加起来 250px+，正文只剩几十像素。）

                **新逻辑**：右栏**自己滚动**，把空间分配交给滚动，而不是让固定块抢。
                  · 移动端 `flex-1 min-h-0 overflow-y-auto`：占满播放器以下全部空间并整体滚动；
                  · 标签正文 `min-h-[22vh]` **保底**（永远读得到），超长由右栏滚动承载；
                  · 走带 `sticky bottom-0` 吸底 —— 整体滚动时播放键不滚走（最高频控件）；
                  · 桌面端全部 `lg:` 兜回原样（横排固定 420px、右栏不滚、正文内部滚）。 */}
        <aside
            data-shot-preview-fields="1"
            /**
             * 🔴 2026-10-16 统一**单一水平基线**（移动端反馈「排版没有对齐」）：
             *   旧状：标签行 0 / 正文与信息块 12px / 镜号条 0 / 走带 8px ⇒ 四种左边缘参差。
             *   现：**水平内边距只属于右栏**（`px-3`），子块一律不再自带 `px-3`
             *       （否则双重缩进：12+12=24px，仍会与镜号条/标签行不齐）。
             */
            className="flex min-h-0 flex-1 w-full flex-col gap-3 overflow-y-auto overscroll-contain px-3 py-1 lg:flex-none lg:w-[420px] lg:shrink-0 lg:overflow-visible"
          >
            {/* 三个标签：动作脚本（默认）/ 场景描述 / 视频提示词 */}
            <div className="flex items-center gap-1" data-shot-preview-tabs="1">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  aria-pressed={tab === t.key}
                  data-shot-preview-tab={t.key}
                  className={cn(
                    "rounded-lg px-2.5 py-1 text-xs font-medium transition-colors",
                    tab === t.key
                      ? "bg-white/90 text-gray-900"
                      : "bg-white/10 text-white/70 hover:bg-white/20"
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* 标签内容（动作脚本默认）—— 🔴 第八轮：长文案按时间码/状态/图片引用分块呈现
                （真库实测：motion_script 3451 条里 3448 条可结构化，715 条切出 ≥3 段时间轴）
                🔴 2026-10-16 移动端 `min-h-[22vh]` **保底**：这是用户要读的主内容
                （动作脚本/场景描述/视频提示词），绝不能被兄弟块挤没；超长部分由右栏整体滚动
                承载。桌面端 `lg:` 恢复「内部滚动」原逻辑（右栏不滚）。 */}
            <div
              data-shot-preview-tab-body={tab}
              className="min-h-[22vh] rounded-xl bg-white/[0.07] py-2.5 lg:min-h-0 lg:flex-1 lg:overflow-y-auto"
            >
              {(() => {
                const cur = TABS.find((t) => t.key === tab)!;
                return (
                  <ShotTextBlocks
                    text={activeShot ? cur.read(activeShot) : ""}
                    empty={cur.empty}
                  />
                );
              })()}
            </div>

            {/* 镜头方向：常显，不折叠。
                🔴 2026-10-17 排版逐块优化（老板：全屏面板「标签与内容挤在一起、遮挡」）：
                  字段块此前只有 `bg-white/[0.05] py-2`，与相邻块在深色背景上糊成一团
                  （看着像文字叠着文字）。现统一 **背景加深到 0.07 + 纵向留白 py-2.5**，
                  让每个字段成为可分辨的一块；标签同时改为**加字距 + 提高对比**（white/60）。
                  ⚠️ 刻意**不加水平内边距**：门禁⑥ 规定「水平基线只属于 aside（px-3），
                     子块自带 px 即双重缩进」—— 所以分块靠**背景浓度**而不是 padding。
                  ⚠️ `shrink-0` 保留（门禁锁「字段栏 shrink-0 只作用于桌面」）。 */}
            <div className="shrink-0 rounded-xl bg-white/[0.07] py-2.5">
              <p className="mb-1.5 text-[11px] font-semibold tracking-wide text-white/60">镜头方向</p>
              <p
                data-shot-preview-field="camera"
                className="text-xs leading-5 text-white/85"
              >
                {activeShot?.cameraDirection || <span className="text-white/40">未填镜头方向</span>}
              </p>
            </div>

            {/* 对白：常显，按 sequence 升序（= 配音/字幕顺序） */}
            <div className="shrink-0 rounded-xl bg-white/[0.07] py-2.5">
              <p className="mb-1.5 text-[11px] font-semibold tracking-wide text-white/60">
                对白{dialogues.length > 0 ? `（${dialogues.length}）` : ""}
              </p>
              <div
                data-shot-preview-field="dialogue"
                className="space-y-1 text-xs leading-5 text-white/85 lg:max-h-[18vh] lg:overflow-y-auto"
              >
                {dialogues.length > 0 ? (
                  dialogues.map((d) => (
                    <div key={d.id} className="flex items-baseline gap-1.5">
                      <MessageCircle className="mt-1 h-3 w-3 shrink-0 self-start text-emerald-400" />
                      <span className="min-w-0 flex-1">
                        <span className="text-white/55">「{d.characterName}」</span>
                        <span>{d.text}</span>
                      </span>
                    </div>
                  ))
                ) : (
                  <span className="text-white/40">本镜无对白</span>
                )}
              </div>
            </div>

            {/* 音效：常显。🔴 不渲染 musicCue（全剧禁乐的合规占位，用户 2026-10-09 拍板） */}
            <div className="shrink-0 rounded-xl bg-white/[0.07] py-2.5">
              <p className="mb-1.5 text-[11px] font-semibold tracking-wide text-white/60">音效</p>
              <ShotTextBlocks
                text={activeShot?.soundDesign}
                empty="未填音效"
                // 🔴 2026-10-17 手机端「音效被截断/看着像与下方叠在一起」修复：
                //   原为 `max-h-[10vh] overflow-y-auto` —— 10vh 在手机上只有约 80px，
                //   装不下音效正文（常 40–80 字），于是半行露出、与下一块视觉重叠，
                //   用户报「层层叠叠、菜单被遮挡」。**改为不限高**：由 aside 整体滚动承载
                //   （与「标签正文」块同一策略），不再在块内制造第二个滚动区。
                //   桌面端仍保留「内部滚动」原行为（用 lg: 限定回来，手机不限高）。
                className="lg:max-h-[10vh] lg:overflow-y-auto"
                // 🔴 标识挂在外层：真机脚本/门禁按 data-shot-preview-field 定位三块常显字段
                wrapperProps={{ "data-shot-preview-field": "sound" }}
              />
            </div>

            {/* 镜号条：一眼看清会经过哪些镜，点哪跳哪（从底部搬上来的）
                🔴 第七轮：① chip 宽度统一 `w-9`（原 `px-2` 随位数变宽，1 位/2 位/3 位参差不齐、
                   1 位数的又窄得点不中）；② 缺号处插占位标记（没出片的镜被 playable 过滤掉了，
                   不标出来用户会以为漏了一镜）。 */}
            {playable.length > 0 && (
              <div
                data-shot-preview-chips="1"
                className="flex min-w-0 shrink-0 items-center gap-1 overflow-x-auto pb-1 sticky bottom-14 z-[9] bg-[#0d0d10]/95 backdrop-blur"
              >
                {/* 🔴 2026-10-17 手机端「镜头号被遮住」修复（老板截图反馈）：
                    走带是 `sticky bottom-0` 的**不透明浮层**，镜号条原本在它上方 ——
                    **只要 aside 还在滚动中，走带就压过镜号条**（要滚到底才看得到），
                    用户读数时镜号被吃掉。现在**镜号条自己也吸底**（`bottom-14` ≈ 走带高度 56px，
                    `z-[9]` 低于走带的 `z-10`），两者常驻底部、上下紧贴、互不遮挡；
                    底色用半透明 + backdrop-blur，避免滚动内容从缝隙里透出来。
                    ⚠️ 门禁锁定的类片段（`flex min-w-0 shrink-0 items-center gap-1 overflow-x-auto`）
                       保持**原文连续**，只在其后追加新类。
                    ⚠️ JSX 注释必须是「花括号包裹的块注释」形态 —— 裸写块注释会让 Turbopack
                       编译失败、整页 500（门禁 8 会拦）。注释正文里也不要直接写花括号，
                       否则同样会提前闭合 JSX 表达式。 */}
                {playable.map((p, i) => {
                  const prevSeq = i > 0 ? playable[i - 1].shot.sequence : null;
                  const gap = prevSeq !== null && p.shot.sequence - prevSeq > 1
                    ? p.shot.sequence - prevSeq - 1
                    : 0;
                  const chip = (
                    <Fragment key={p.shot.id}>
                      {gap > 0 && (
                        <span
                          data-shot-chip-gap={gap}
                          title={`镜 ${prevSeq} 与 ${p.shot.sequence} 之间有 ${gap} 镜尚未出片`}
                          className="flex h-7 w-9 shrink-0 items-center justify-center rounded-md border border-dashed border-white/20 font-mono text-[10px] text-white/40"
                        >
                          +{gap}
                        </span>
                      )}
                      <button
                        type="button"
                        title={freshSrcs.has(p.src) ? `镜 ${p.shot.sequence}（新同步）` : `镜 ${p.shot.sequence}`}
                        onClick={() => {
                          touchedRef.current = true;
                          clearFresh(p.src); // 点它就消 new（用户原话：点击…后消失）
                          onActiveShotChange(p.shot.id);
                          setPlaying(true);
                        }}
                        data-shot-chip={p.shot.sequence}
                        data-shot-chip-new={freshSrcs.has(p.src) ? "1" : undefined}
                        className={cn(
                          // 🔴 w-9 固定宽度：可点面积一致，且三位镜号（100+）也不撑破布局
                          "relative h-7 w-9 shrink-0 rounded-md font-mono text-[11px] tabular-nums transition-colors",
                          i === activeIdx
                            ? "bg-primary text-white"
                            : "bg-white/10 text-white/70 hover:bg-white/25"
                        )}
                      >
                        {p.shot.sequence}
                        {/* 🔴 新同步进来的片：右上角红点（点它或播到它即消失） */}
                        {freshSrcs.has(p.src) && (
                          <span
                            data-shot-chip-new-dot="1"
                            className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-[#0d0d10]"
                          />
                        )}
                      </button>
                    </Fragment>
                  );
                  return chip;
                })}
              </div>
            )}

            {/* 走带：从底部整块搬上来的（播放键仍在中间）
                🔴 2026-10-16 移动端 `sticky bottom-0`：右栏整体滚动后，走带（含播放键）若跟着
                   滚走，用户每次都得滚到底才能暂停 —— 它是最高频控件，必须常驻。
                   底色改为不透明（`bg-[#16161c]`），否则吸底时下方内容会透出来糊在一起。 */}
            <div
              data-shot-preview-transport="1"
              className="sticky bottom-0 z-10 grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-xl bg-[#16161c] px-2 py-2 shadow-[0_-8px_16px_rgba(0,0,0,0.5)] lg:static lg:bg-white/[0.07] lg:shadow-none"
            >
              {/* 左：静音 */}
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={toggleMuted}
                  aria-label={muted ? "打开声音" : "静音"}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25"
                >
                  {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </button>
              </div>

              {/* 中：上一 / **播放键** / 下一 */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => jump(-1)}
                  disabled={playable.length === 0 || activeIdx === 0}
                  aria-label="上一片段"
                  title="上一片段（←）"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25 disabled:opacity-30"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>

                {/* 🔴 中央主键只管「当前这一镜播/停」；连播是右侧独立开关 */}
                <button
                  type="button"
                  onClick={togglePlay}
                  disabled={!activeSrc}
                  aria-label={playing ? "暂停" : "播放"}
                  data-shot-preview-play="1"
                  className={cn(
                    "flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-all disabled:opacity-40",
                    playing ? "bg-white text-gray-900" : "bg-primary text-white hover:brightness-110"
                  )}
                >
                  {playing ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
                </button>

                <button
                  type="button"
                  onClick={() => jump(1)}
                  disabled={playable.length === 0 || isLast}
                  aria-label="下一片段"
                  title="下一片段（→）"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25 disabled:opacity-30"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>

              {/* 右：连播开关 */}
              <div className="flex items-center justify-end">
                <button
                  type="button"
                  role="switch"
                  aria-checked={loopNext}
                  aria-label="连播开关"
                  title="开启后本镜播完自动播下一镜"
                  data-shot-preview-loop="1"
                  onClick={() => setLoopNext((v) => !v)}
                  className="flex items-center gap-1.5 rounded-full bg-white/10 px-2 py-1.5 transition-colors hover:bg-white/20"
                >
                  <span
                    data-shot-preview-loop-track={loopNext ? "on" : "off"}
                    className={cn(
                      "relative h-3.5 w-6 rounded-full transition-colors",
                      loopNext ? "bg-primary" : "bg-white/25"
                    )}
                  >
                    <span
                      className={cn(
                        "absolute top-0.5 h-2.5 w-2.5 rounded-full bg-white transition-all",
                        loopNext ? "left-3" : "left-0.5"
                      )}
                    />
                  </span>
                  <span className="text-[11px] font-semibold text-white/80">连播</span>
                </button>
              </div>
            </div>
          </aside>
      </div>

      {/* 快捷键提示（走带搬上右栏后，这里是它的落点） */}
      <div className="flex items-center justify-center border-t border-white/10 px-4 py-1.5 text-[11px] text-white/35">
        ←/→ 切片段 · 空格 播放/暂停 · F 全屏 · Esc 退出全屏/关闭
      </div>
    </div>
  );
}
